import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Modal from 'src/components/layouts/Modal/Modal';
import Input from 'src/components/ui/Input/Input';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Badge from 'src/components/ui/Badge/Badge';
import Icon from 'src/components/ui/Icon/Icon';
import { useWorkflowBlueprintsQuery, WorkflowBlueprint, WorkflowItemModel } from 'src/services/workflows';
import { useProfileQuery } from 'src/services/user';
import { getColorForTriggerCategory, getIconForTriggerCategory } from 'src/utils/general';
import BlueprintPreviewModal from '../BlueprintPreviewModal/BlueprintPreviewModal';
import styles from './CreateWorkflowOverlay.module.scss';

const CATEGORIES = ['outbound', 'inbound', 'inventory', 'returns', 'integrations'];

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export default function CreateWorkflowOverlay({ isOpen, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [previewBlueprint, setPreviewBlueprint] = useState<WorkflowBlueprint | null>(null);
  const { data: blueprints = [] } = useWorkflowBlueprintsQuery(isOpen);
  const { data: profile } = useProfileQuery();

  const withDefaultRecipients = (item: WorkflowItemModel): WorkflowItemModel => {
    const related: any = item.related_object || {};
    const details = related.detail_values || {};
    let relatedObject = item.related_object;
    if (item.item_type === 'action' && related.action_type === 'mail' && !details.recipient_email) {
      relatedObject = { ...related, detail_values: { ...details, recipient_email: profile?.email || '' } };
    } else if (item.item_type === 'action' && related.action_type === 'notification' && !details.recipient_user_id) {
      relatedObject = {
        ...related,
        detail_values: { ...details, recipient_user_id: profile?.id, recipient_user_name: profile?.name },
      };
    }
    return {
      ...item,
      related_object: relatedObject,
      true_result: item.true_result ? withDefaultRecipients(item.true_result) : item.true_result,
      false_result: item.false_result ? withDefaultRecipients(item.false_result) : item.false_result,
    };
  };

  const startCustom = () => {
    onClose();
    navigate('/workflows/create');
  };

  const applyBlueprint = (blueprint: WorkflowBlueprint) => {
    setPreviewBlueprint(null);
    onClose();
    navigate('/workflows/create', {
      state: {
        root: withDefaultRecipients(blueprint.root),
        name: blueprint.name,
        description: blueprint.description ?? '',
        oneTime: blueprint.one_time,
        triggerName: blueprint.trigger.name,
      },
    });
  };

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return blueprints.filter(
      bp =>
        (!category || bp.category === category) &&
        (!term || bp.name.toLowerCase().includes(term) || (bp.description || '').toLowerCase().includes(term)),
    );
  }, [blueprints, category, search]);

  const availableCategories = CATEGORIES.filter(key => blueprints.some(bp => bp.category === key));

  return (
    <Modal isOpen={isOpen} onClose={onClose} hasCloseButton hasDefaultPadding wide>
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className="typo-title-24">{t('workflows.create_overlay.title')}</h2>
          <p className="typo-text-14 text-faded">{t('workflows.create_overlay.subtitle')}</p>
        </div>

        <div className={styles.toolbar}>
          <Input
            type="text"
            icon="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('workflows.create_overlay.search_placeholder')}
            className={styles.search}
            aria-label={t('workflows.create_overlay.search_placeholder')}
          />
          {availableCategories.length > 0 && (
            <div className={styles.categories}>
              <button
                type="button"
                className={`${styles.categoryChip} ${category === null ? styles.categoryChipActive : ''}`}
                onClick={() => setCategory(null)}>
                {t('workflows.all')}
              </button>
              {availableCategories.map(key => (
                <button
                  key={key}
                  type="button"
                  className={`${styles.categoryChip} ${category === key ? styles.categoryChipActive : ''}`}
                  style={{ color: getColorForTriggerCategory(key) }}
                  onClick={() => setCategory(category === key ? null : key)}>
                  {t(`workflows.trigger_category.${key}`)}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={styles.cardsGrid}>
          <div
            className={`${styles.card} ${styles.customCard}`}
            role="button"
            tabIndex={0}
            onClick={startCustom}
            onKeyDown={e => e.key === 'Enter' && startCustom()}>
            <div className={styles.cardHead}>
              <span className={styles.cardIcon}>
                <Icon name="plus" width={18} height={18} />
              </span>
            </div>
            <span className={styles.cardTitle}>{t('workflows.create_overlay.custom_workflow_title')}</span>
            <span className={styles.cardText}>{t('workflows.create_overlay.custom_workflow_subtitle')}</span>
            <div className={styles.cardFooter}>
              <Button tone={ButtonToneProps.SECONDARY} title={t('workflows.create_overlay.start_blank')} />
            </div>
          </div>
          {visible.map(bp => (
            <div key={bp.id} className={styles.card}>
              <div className={styles.cardHead}>
                <span className={styles.cardIcon} style={{ background: getColorForTriggerCategory(bp.category) }}>
                  <Icon name={getIconForTriggerCategory(bp.category)} width={18} height={18} className="text-white" />
                </span>
                <Badge
                  expanded
                  label={bp.trigger.name}
                  colorOverride={getColorForTriggerCategory(bp.category)}
                  className={styles.triggerBadge}
                />
              </div>
              <span className={styles.cardTitle}>{bp.name}</span>
              <span className={styles.cardText}>{bp.description}</span>
              <div className={styles.cardFooter}>
                <Button
                  tone={ButtonToneProps.SECONDARY}
                  title={t('workflows.create_overlay.preview')}
                  onClick={() => setPreviewBlueprint(bp)}
                />
                <Button
                  tone={ButtonToneProps.PRIMARY_BLUE}
                  title={t('workflows.create_overlay.use_blueprint')}
                  onClick={() => applyBlueprint(bp)}
                />
              </div>
            </div>
          ))}
        </div>
        {blueprints.length > 0 && visible.length === 0 && (
          <span className="typo-text-14 text-faded">{t('workflows.create_overlay.no_blueprints')}</span>
        )}
      </div>
      <BlueprintPreviewModal
        blueprint={previewBlueprint}
        onClose={() => setPreviewBlueprint(null)}
        onUse={applyBlueprint}
      />
    </Modal>
  );
}
